import _, { upperCase } from "lodash";
import { Select, TableColumnsType } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getExamLevels } from '../../../api/statistics.api';
import useAxios from '../../../hooks/useAxios';
import {
  IExamStatsByAnswerRate,
  IGetExamLevels,
} from '../../../models/statistics.model';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import SelectExamSubject from '../SelectExamSubject';
import ExamAnswerRateTable from "./ExamAnswerRateTable";
import moment from "moment";
import exportTitleTable from "../../../lib/exportTitleTable";
import DownloadButton from "../../DownloadButton";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import { getDetailCode, getDetailCodeLabel } from "../../../lib/util";
import Radio from "../../Radio";
import ExamAnswerRateGraph from "./ExamAnswerRateGraph";
import Button from "../../Button";

export enum EResultType {
  Table,
  Graph,
};

interface IProps {
  data: IExamStatsByAnswerRate[] | undefined;
  examCode: number | undefined;
  semesterCode: number | undefined;
  examSubjectCode: number | undefined;
  setExamSubjectCode: (examSubjectCode: number | undefined) => void;
  courseCode: number | undefined;
  setCourseCode: (courseCode: number | undefined) => void;
}

const ExamStatsByAnswerRate: FC<IProps> = ({
  data,
  examCode,
  semesterCode,
  examSubjectCode,
  setExamSubjectCode,
  courseCode,
  setCourseCode,
}) => {
  const { t } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const TE0001 = getDetailCode(commonCodes, 'TE0001');
  const { data: res, isLoading, error, execute } = useAxios<IGetExamLevels>();
  const [selCourseList, setSelCourseList] = useState<IGetExamLevels>();
  const [hasEAnswer, setHasEAnswer] = useState(false);
  const [resData, setResData] = useState<IExamStatsByAnswerRate[]>([]);
  const [testStudents, setTestStudents] = useState<number>(0);
  const [testPaper, setTestPaper] = useState<string>('');
  const [courseName, setCourseName] = useState<string>('');
  const [examSubjectName, setExamSubjectName] = useState<string>('');
  const [excelExamDiv, setExcelExamDiv] = useState<string>('');
  const [excelSemesterName, setExcelSemesterName] = useState<string>('');
  const [excelExamTypeName, setExcelExamTypeName] = useState<string>('');
  const [excelMonthNameCode, setExcelMonthNameCode] = useState<string>('');
  const [resultType, setResultType] = useState<EResultType>(EResultType.Table);

  useEffect(() => {
    setSelCourseList(undefined);

    if (examCode && semesterCode) {
      execute(getExamLevels(examCode, semesterCode));
    }
  }, [examCode, semesterCode]);

  useEffect(() => {
    let _data: any = _.cloneDeep(res);

    setSelCourseList(_data);
  }, [res]);

  useEffect(() => {
    let tempData: any = _.cloneDeep(data);
    let tempTestStudents: number = 0;
    let tempTestPaper: string = '';
    let tempExamDiv: string = '';
    let tempSemesterName: string = '';
    let tempExamTypeName: string = '';
    let tempMonthNameCode: string = '';
    let tempHasEAnswer = false;

    if (tempData) {
      if(tempData.length) {
        tempData.forEach((item: any, idx: number) => {
          item.rowIdx = idx + 1;
          item.discrimination = (Math.floor(item.correctTopAnswerRate) - Math.floor(item.correctBottomAnswerRate)) / 100;
          //답안 Highlight용
          if(upperCase(item.correctAnswerHigh) === 'A') {
            item.answerPera = '|||H|||' + item.answerPera;
          } else if(upperCase(item.correctAnswerHigh) === 'B') {
            item.answerPerb = '|||H|||' + item.answerPerb;
          } else if(upperCase(item.correctAnswerHigh) === 'C') {
            item.answerPerc = '|||H|||' + item.answerPerc;
          } else if(upperCase(item.correctAnswerHigh) === 'D') {
            item.answerPerd = '|||H|||' + item.answerPerd;
          } else if(upperCase(item.correctAnswerHigh) === 'E') {
            item.answerPere = '|||H|||' + item.answerPere;
          }

          //시험 학생 수
          if(!tempTestStudents) {
            tempTestStudents = item.testStudents;
          }

          //EDUBASE 시험지 명
          if(!tempTestPaper) {
            tempTestPaper = item.testPaper + '(code : ' + item.testPaperCode + ')';
          }

          //보기 E 문제 여부 체크
          if(idx === 0 && item.quizType !== 41) {
            tempHasEAnswer = true;
          }

          //시험 구분
          if(!tempExamDiv) {
            tempExamDiv = item.examDiv;
            if(item.examDiv === 'MT') {
              if(item.eduStepCode === 'MD') {
                tempExamDiv = 'PL'
              }
            }
          }

          //학기
          if(!tempSemesterName) {
            tempSemesterName = item.semesterName;
          }

          //시험타입
          if(!tempExamTypeName) {
            tempExamTypeName = item.examTypeName;
          }

          //월
          if(!tempMonthNameCode) {
            tempMonthNameCode = item.monthNameCode;
          }

        });
      }
    }

    setTestStudents(tempTestStudents);
    setTestPaper(tempTestPaper);
    setResData(tempData);
    setExcelExamDiv(getDetailCodeLabel(TE0001, tempExamDiv));
    setExcelSemesterName(tempSemesterName);
    setExcelExamTypeName(tempExamTypeName);
    setExcelMonthNameCode(tempMonthNameCode);
    setHasEAnswer(tempHasEAnswer);
  }, [data]);

  const getColumns = () => {
    const mainColumns: TableColumnsType<IExamStatsByAnswerRate> = [
      {
        title: t('statistics.placementTest.column8'),
        dataIndex: 'rowIdx'
      },
      {
        title: 'Skill',
        dataIndex: 'skillName',
      },
      {
        title: t('statistics.placementTest.column11'),
        dataIndex: 'correctAnswerRate',
        key: 'correctAnswerRate',
        align: 'center',
      },
      {
        title: t('statistics.regularTest.column3'),
        children: [
          {
            title: 'Top(27%)',
            dataIndex: 'correctTopAnswerRate',
          },
          {
            title: 'Bottom(27%)',
            dataIndex: 'correctBottomAnswerRate',
          },
          {
            title: t('statistics.regularTest.column5'),
            dataIndex: 'discrimination',
          },
        ]
      },
      
    ];

    const addColumnsDefault: TableColumnsType<IExamStatsByAnswerRate> = [
      {
        title: t('statistics.regularTest.column4'),
        children: [
          {
            title: 'A',
            dataIndex: 'answerPera',
          },
          {
            title: 'B',
            dataIndex: 'answerPerb',
          },
          {
            title: 'C',
            dataIndex: 'answerPerc',
          },
          {
            title: 'D',
            dataIndex: 'answerPerd',
          },
        ]
      },     
    ];

    const addColumnsHasE: TableColumnsType<IExamStatsByAnswerRate> = [
      {
        title: t('statistics.regularTest.column4'),
        children: [
          {
            title: 'A',
            dataIndex: 'answerPera',
          },
          {
            title: 'B',
            dataIndex: 'answerPerb',
          },
          {
            title: 'C',
            dataIndex: 'answerPerc',
          },
          {
            title: 'D',
            dataIndex: 'answerPerd',
          },
          {
            title: 'E',
            dataIndex: 'answerPere',
          },
        ]
      },     
    ];

    if(hasEAnswer) {
      return _.concat(mainColumns, addColumnsHasE);
    } else {
      return _.concat(mainColumns, addColumnsDefault);
    }
  }

  const onExportTable = () => {
    const currentDate = moment(new Date());

    let excelTitle = `${t('statistics.regularTest.title1')}(${t('statistics.placementTest.tab1')}) - ${excelSemesterName} ${excelMonthNameCode}${t('class.class.text26')} ${excelExamDiv} (${excelExamTypeName})`;
    let arrSubTitle = [];

    let courseList = '';
    if(!courseName) {
      selCourseList?.data?.map((item, idx) => {
        if(idx === 0) {
          courseList += item.courseName;  
        } else {
          courseList += ',' + item.courseName;
        }
      });
    } else {
      courseList = courseName;
    }

    arrSubTitle.push(t('statistics.regularTest.placeholder3') + " : " + examSubjectName + (courseList?'(' + courseList + ')':''));
    arrSubTitle.push('Test Students : ' + testStudents);
    arrSubTitle.push('Test Paper : ' + testPaper);

    exportTitleTable(getColumns(), resData, `${t('statistics.regularTest.title1')}(${t('statistics.placementTest.tab1')})_(${currentDate.format('YYYYMMDD')})`, `${t('statistics.placementTest.tab1')}`, [10, 25, 15, 15, 15, 15, 15, 15, 15, 15, 15], excelTitle, arrSubTitle, true);
  }

  return (
    <>
      {/*isLoading && <Loader />*/}
      <ErrorResponse error={error} />

      <div className="comm-bx__between">
        <div className="comm-btn__area">
        {data &&
          <>
            <Button 
              className={resultType === EResultType.Table ? 'btn-basic pink-line' : 'btn-basic gray-line'}
              text={t('statistics.regularTest.9')}
              onClick={()=>{ setResultType(EResultType.Table) }}
            ></Button>
            <Button 
              className={resultType === EResultType.Graph ? 'btn-basic pink-line' : 'btn-basic gray-line'}
              text={t('statistics.regularTest.10')}
              onClick={()=>{ setResultType(EResultType.Graph) }}
            ></Button>
          </>
        }
        </div>
        <div className="comm-btn__area right">
          <div className="w-150p">
            <SelectExamSubject
              examCode={examCode}
              examSubjectCode={examSubjectCode}
              setExamSubjectCode={setExamSubjectCode}
              setExamSubjectName={setExamSubjectName}
            />
          </div>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('statistics.dttt.column1')}
              value={courseCode}
              onChange={(value, options) => {
                setCourseCode(value)
                Object.entries(options).map(([key, value])=>{
                  if(key === 'courseName') {
                    setCourseName(value);
                  }
                });
              }}
            >
              <Select.Option value="" courseName=''>{t('common.text.all')}</Select.Option>
              {selCourseList?.data?.map((item) => (
                <Select.Option key={item.courseCode} value={item.courseCode} courseName={item.courseName}>
                  {item.courseName}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </div>
      
      
      { resultType === EResultType.Table && <ExamAnswerRateTable data={data} hasEAnswer={hasEAnswer} /> }
      { resultType === EResultType.Graph && <ExamAnswerRateGraph data={data} />}
      {data && resultType === EResultType.Table &&
        <div className="comm-bx__space">
          <DownloadButton onClick={onExportTable} />
        </div>
      }
    </>
  );
};

export default ExamStatsByAnswerRate;
